// @flow
import StringAttributeModel from "./StringAttributeModel";
import ConstraintCollection from "./input-constraints/ConstraintCollection";

import PasswordLowerAndUpperCaseConstraint from "./input-constraints/PasswordLowerAndUpperCaseConstraint";
import PasswordThreeConsecutiveCharactersNotAllowedConstraint from "./input-constraints/PasswordThreeConsecutiveCharactersNotAllowedConstraint";
import PasswordMinNumericCharactersConstraint from "./input-constraints/PasswordMinNumericCharactersConstraint";
import PasswordMinSpecialCharactersConstraint from "./input-constraints/PasswordMinSpecialCharactersConstraint";
import RegexConstraint from "./input-constraints/RegexConstraint";
import PasswordConfirmConstraint from "./input-constraints/PasswordConfirmConstraint";

import { CONFIRM_PASSWORD } from "../../constants/LayoutHints";

import type { RegexObject } from "./input-constraints/RegexConstraint";

/**
 * Password attribute
 */
export default class PasswordAttributeModel extends StringAttributeModel {
  _confirmValue: string;
  _isConfirmPassword: boolean;
  _otherLabel: string;

  /**
   */
  static isApplicableModel(contributions: Object): boolean {
    const layouthint = contributions?.layouthint;
    return (
      contributions.type === "password" ||
      (Array.isArray(layouthint) && layouthint.includes("password"))
    );
  }

  /**
   */
  get type(): string {
    return "password";
  }

  /**
   * Update the attribute by name and value
   */
  update(value: string): PasswordAttributeModel {
    this.updateLastModification();
    this.inputvalue = value;

    return this;
  }

  /**
   */
  get constraints(): Object {
    return this.getContribution("constraints", {});
  }

  /**
   */
  get upperAndLowerCaseMandatory(): ?boolean {
    return this.constraints.upperAndLowerCaseMandatory;
  }

  /**
   */
  get maxSequenceOfIdenticalCharacters(): ?number {
    return this.constraints.maxSequenceOfIdenticalCharacters;
  }

  /**
   */
  get maxSequenceOfUsernameCharacters(): ?number {
    return this.constraints.maxSequenceOfUsernameCharacters;
  }

  /**
   */
  get minNumberOfNumericCharacters(): ?number {
    return this.constraints.minNumberOfNumericCharacters;
  }

  /**
   */
  get minNumberOfSpecialCharacters(): ?number {
    return this.constraints.minNumberOfSpecialCharacters;
  }

  /**
   */
  get regexConstraints(): ?Array<RegexObject> {
    return this.constraints.regexConstraint;
  }

  /**
   * Add password constraints
   */
  addConstraints(): ConstraintCollection {
    if (this.isConfirmPassword) {
      return this.getConfirmPasswordConstraints();
    }
    return this.getStandardPasswordConstraints();
  }

  /**
   * Constraints for a confirmation password
   */
  getConfirmPasswordConstraints(): ConstraintCollection {
    const constraints = new ConstraintCollection();
    constraints.add(
      new PasswordConfirmConstraint(this.confirmValue, this.otherLabel),
    );
    return constraints;
  }

  /**
   * Constrains for a standard password
   */
  getStandardPasswordConstraints(): ConstraintCollection {
    const constraints = new ConstraintCollection();

    if (this.upperAndLowerCaseMandatory) {
      constraints.add(new PasswordLowerAndUpperCaseConstraint());
    }

    if (this.maxSequenceOfIdenticalCharacters) {
      constraints.add(
        new PasswordThreeConsecutiveCharactersNotAllowedConstraint(
          this.maxSequenceOfIdenticalCharacters,
        ),
      );
    }

    if (this.minNumberOfNumericCharacters) {
      constraints.add(
        new PasswordMinNumericCharactersConstraint(
          this.minNumberOfNumericCharacters,
        ),
      );
    }

    if (this.minNumberOfSpecialCharacters) {
      constraints.add(
        new PasswordMinSpecialCharactersConstraint(
          this.minNumberOfSpecialCharacters,
        ),
      );
    }

    if (this.regexConstraints && this.regexConstraints.length > 0) {
      this.regexConstraints.forEach((regexConstraint) => {
        constraints.add(new RegexConstraint(regexConstraint));
      });
    }

    if (
      this.layouthint.has(CONFIRM_PASSWORD) &&
      this.isConfirmPassword == null
    ) {
      constraints.add(
        new PasswordConfirmConstraint(this.confirmValue, this.otherLabel),
      );
    }

    return constraints;
  }

  /**
   * Retrieve applicable constraint for this attribute
   */
  get constraintCollection(): ConstraintCollection {
    if (this.isConfirmPassword) {
      // confirm password only needs local constraints, no server constraints
      return this.getConfirmPasswordConstraints();
    }

    return super.constraintCollection;
  }

  /**
   */
  get isConfirmPassword(): boolean | null {
    return this._isConfirmPassword;
  }

  /**
   */
  set isConfirmPassword(isConfirmPassword: boolean) {
    this._isConfirmPassword = isConfirmPassword;
  }

  /**
   */
  get confirmValue(): string {
    return this._confirmValue || "";
  }

  /**
   */
  set confirmValue(confirmValue: string) {
    this._confirmValue = confirmValue;

    this.validate(this.inputvalue);
  }

  /**
   */
  get otherLabel(): string {
    return this._otherLabel;
  }

  /**
   */
  set otherLabel(otherLabel: string) {
    this._otherLabel = otherLabel;
  }

  /**
   * Validate input
   */
  validate(value: string): boolean {
    // when client side validation is disabled, this attribute is always valid
    if (!this.useClientsideValidation()) {
      return true;
    }

    if (this.isOptionalAndEmpty(value)) {
      this.setIsValid(true);
    } else if (this._validatedValue !== `${this.confirmValue}-${value}`) {
      this.setIsValid(this.constraintCollection.validate(value));
    }
    this._validatedValue = `${this.confirmValue}-${value}`;

    return this.getIsValid();
  }

  /**
   */
  getFormData(): { [string]: any } | null {
    if (this.isConfirmPassword || this.inError()) {
      return null;
    }

    return {
      [this.name]: this.value,
    };
  }
}
